import React, { useState, type FC } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Form, { Field, type FieldProps } from '@atlaskit/form';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import InlineDialog from '@atlaskit/inline-dialog';

import {
	MARKDOWN_SUFFIX,
	endsWithMarkdownSuffix,
} from '@confluence/content-markdown-mode/entry-points/markdownSuffix.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import {
	EditorTitleTextArea,
	EditorTitleTextAreaNext,
	AIGeneratedEditorTitleTextArea,
} from './editorTitleStyled.tsx';
import { EditorTitleContainer } from './shared-components/EditorTitleContainer.tsx';

const titleStyles = xcss({
	width: '100%',
});

const errorIconStyles = xcss({
	flexShrink: 0,
	margin: 'space.050',
	marginBottom: 'space.0',
});

const fieldStyles = xcss({
	// This removes the default top margin that comes with the atlas Field component.
	marginTop: 'space.negative.100',
});

export type LivePagesTitlePresentationProps = {
	title: string;
	isDisabled?: boolean;
	viewMode?: boolean;
	isTitleCenterAligned?: boolean;
	isUserEditing: boolean;
	isTitleAIGenerated?: boolean;
	updateError: any;
	updateLoading: boolean;
	handleBlur: (
		e: React.FocusEvent<HTMLTextAreaElement>,
		fieldProps: FieldProps<string, HTMLTextAreaElement>,
	) => void;
	handleFocus: () => void;
	handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>, fieldProps: any) => void;
	handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	getIsInvalidTitle: (title: string | undefined) => boolean;
	buildErrorMessage: (fieldProps: any, error: any) => string;
	textAreaRef: React.RefObject<HTMLTextAreaElement>;
	formatMessage: (obj: any) => string;
	i18n: any;
	AI_SUGGEST_TITLE_PROPS: any;
	maxLength: number;
	isDisabledTextBlack: boolean;
	isCssAutoSizingSupported?: boolean;
	customPlaceholder?: string;
	isMarkdownMode?: boolean;
};

export const LivePagesTitlePresentation: FC<LivePagesTitlePresentationProps> = ({
	title,
	isDisabled,
	viewMode,
	isTitleCenterAligned,
	isUserEditing,
	isTitleAIGenerated,
	updateError,
	updateLoading,
	handleBlur,
	handleFocus,
	handleChange,
	handleKeyDown,
	getIsInvalidTitle,
	buildErrorMessage,
	textAreaRef,
	formatMessage,
	i18n,
	AI_SUGGEST_TITLE_PROPS,
	maxLength,
	isCssAutoSizingSupported,
	isDisabledTextBlack,
	customPlaceholder,
	isMarkdownMode,
}) => {
	// Render an immutable `.md` suffix when in markdown mode and the user is not editing the title.
	// The suffix is purely visual: `fieldProps.value` is left untouched, so saved title state never includes it.
	// Skip the append when the raw value already ends with `.md` (trim-tolerant); literal `.md` titles should not become `.md.md`.
	const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
	const getDisplayValue = (rawValue: string): string =>
		isMarkdownMode && !isTextAreaFocused && rawValue.length > 0 && !endsWithMarkdownSuffix(rawValue)
			? `${rawValue}${MARKDOWN_SUFFIX}`
			: rawValue;
	// If the browser supports it, we use a textarea that auto resizes using CSS so we don't need to adjust the height in JS.
	// This is better for performance as it avoids reflows and layout shifts.
	const TextAreaComponent = isCssAutoSizingSupported
		? EditorTitleTextAreaNext
		: EditorTitleTextArea;

	const TextAreaComponentToUse = isTitleAIGenerated
		? AIGeneratedEditorTitleTextArea
		: TextAreaComponent;
	const placeholder: string = customPlaceholder ?? formatMessage(i18n.placeholder);

	return (
		<Box xcss={titleStyles}>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form onSubmit={() => {}}>
					{/* the content-title-id id is required for useIsTitleIntersectingWithHeader */}
					<Box xcss={fieldStyles} id="content-title-id">
						<Field name="livepages-title" defaultValue={title}>
							{({ fieldProps, error }: any) => (
								<>
									<InlineDialog
										isOpen={
											!isDisabled &&
											!!(
												fieldProps.isInvalid ||
												(!updateLoading && updateError && !isUserEditing) ||
												getIsInvalidTitle(fieldProps?.value)
											)
										}
										content={
											<Inline space="space.025" alignBlock="start">
												<Box xcss={errorIconStyles}>
													<StatusErrorIcon
														label="error"
														spacing="none"
														color={token('color.icon.danger')}
													/>
												</Box>
												{buildErrorMessage(fieldProps, error)}
											</Inline>
										}
										placement={isTitleCenterAligned ? 'bottom' : 'bottom-start'}
										strategy="absolute"
									>
										<EditorTitleContainer>
											<TextAreaComponentToUse
												{...fieldProps}
												{...AI_SUGGEST_TITLE_PROPS}
												value={getDisplayValue(fieldProps.value)}
												data-testid={`editor-title-text-area${isCssAutoSizingSupported ? '-css-auto-sizing' : ''}`}
												minimumRows={1}
												rows={1}
												{...(expValEquals('a11y-fixes-week2-may-2026', 'isEnabled', true)
													? { role: 'heading', 'aria-level': 1 }
													: {})}
												isTitleCenterAligned={isTitleCenterAligned}
												isTitleAIGenerated={isTitleAIGenerated}
												isInvalid={error}
												disabled={isDisabled}
												readOnly={viewMode}
												aria-readonly={viewMode}
												aria-disabled={isDisabled}
												ref={textAreaRef}
												maxLength={maxLength}
												aria-label={placeholder}
												placeholder={placeholder}
												shouldAnimate={false}
												autoComplete="off"
												resize="vertical"
												onChange={(e) => handleChange(e, fieldProps)}
												onFocus={() => {
													setIsTextAreaFocused(true);
													handleFocus();
												}}
												onBlur={(e) => {
													setIsTextAreaFocused(false);
													handleBlur(e, fieldProps);
												}}
												onKeyDown={handleKeyDown}
												isDisabledTextBlack={isDisabledTextBlack}
											/>
										</EditorTitleContainer>
									</InlineDialog>
								</>
							)}
						</Field>
					</Box>
				</Form>
			) : (
				<Form onSubmit={() => {}}>
					{({ formProps }) => (
						<form {...formProps}>
							{/* the content-title-id id is required for useIsTitleIntersectingWithHeader */}
							<Box xcss={fieldStyles} id="content-title-id">
								<Field name="livepages-title" defaultValue={title}>
									{({ fieldProps, error }: any) => (
										<>
											<InlineDialog
												isOpen={
													!isDisabled &&
													!!(
														fieldProps.isInvalid ||
														(!updateLoading && updateError && !isUserEditing) ||
														getIsInvalidTitle(fieldProps?.value)
													)
												}
												content={
													<Inline space="space.025" alignBlock="start">
														<Box xcss={errorIconStyles}>
															<StatusErrorIcon
																label="error"
																spacing="none"
																color={token('color.icon.danger')}
															/>
														</Box>
														{buildErrorMessage(fieldProps, error)}
													</Inline>
												}
												placement={isTitleCenterAligned ? 'bottom' : 'bottom-start'}
												strategy="absolute"
											>
												<EditorTitleContainer>
													<TextAreaComponentToUse
														{...fieldProps}
														{...AI_SUGGEST_TITLE_PROPS}
														value={getDisplayValue(fieldProps.value)}
														data-testid={`editor-title-text-area${isCssAutoSizingSupported ? '-css-auto-sizing' : ''}`}
														minimumRows={1}
														rows={1}
														{...(expValEquals('a11y-fixes-week2-may-2026', 'isEnabled', true)
															? { role: 'heading', 'aria-level': 1 }
															: {})}
														isTitleCenterAligned={isTitleCenterAligned}
														isTitleAIGenerated={isTitleAIGenerated}
														isInvalid={error}
														disabled={isDisabled}
														readOnly={viewMode}
														aria-readonly={viewMode}
														aria-disabled={isDisabled}
														ref={textAreaRef}
														maxLength={maxLength}
														aria-label={placeholder}
														placeholder={placeholder}
														shouldAnimate={false}
														autoComplete="off"
														resize="vertical"
														onChange={(e) => handleChange(e, fieldProps)}
														onFocus={() => {
															setIsTextAreaFocused(true);
															handleFocus();
														}}
														onBlur={(e) => {
															setIsTextAreaFocused(false);
															handleBlur(e, fieldProps);
														}}
														onKeyDown={handleKeyDown}
														isDisabledTextBlack={isDisabledTextBlack}
													/>
												</EditorTitleContainer>
											</InlineDialog>
										</>
									)}
								</Field>
							</Box>
						</form>
					)}
				</Form>
			)}
		</Box>
	);
};
